import { useMemo } from "react"
import { useQuery } from "urql"

import {
  GetPriceChangeSubmissionDocument,
  type PriceChangeQuoteRequestProduct,
  type PriceChangeSubmission,
  type Query,
} from "@/graphql/codegen/graphql.ts"
import { PRICE_CHANGE_PAGE_SIZE, PriceChangeTableVariant } from "@/screens/Pricing/constants"
import { getPriceChangeItemsNormalizer } from "@/screens/Pricing/utils/normalize"

interface UseGetPriceChangeSubmissionParams {
  priceChangeId: string
  page?: number
  limit?: number
}

interface UseGetPriceChangeSubmissionReturn {
  priceChangeSubmission?: PriceChangeSubmission
  quoteProductByProductId: Map<number, PriceChangeQuoteRequestProduct>
  total: number
  pageCount: number
  isFetching: boolean
  refetch: () => void
}

export const useGetPriceChangeSubmission = ({
  priceChangeId,
  page = 1,
  limit = PRICE_CHANGE_PAGE_SIZE,
}: UseGetPriceChangeSubmissionParams): UseGetPriceChangeSubmissionReturn => {
  const [{ data, fetching }, reexecuteQuery] = useQuery<Pick<Query, "priceChange">>({
    query: GetPriceChangeSubmissionDocument,
    variables: {
      input: { submissionId: priceChangeId, offset: (page - 1) * limit, limit },
    },
    pause: !priceChangeId,
  })

  const refetch = () => reexecuteQuery({ requestPolicy: "network-only" })

  const { priceChangeSubmission, total } = useMemo(() => {
    const output = data?.priceChange?.getPriceChangeSubmission
    if (output?.__typename !== "GetPriceChangeSubmissionSuccess") {
      return { priceChangeSubmission: undefined, total: 0 }
    }

    if (output.priceChangeSubmission.id !== priceChangeId) {
      return { priceChangeSubmission: undefined, total: 0 }
    }

    const normalize = getPriceChangeItemsNormalizer(PriceChangeTableVariant.ReviewSubmission)
    return {
      priceChangeSubmission: {
        ...output.priceChangeSubmission,
        priceChangeItems: normalize(output.priceChangeSubmission.priceChangeItems),
      },
      total: output.pageInfo.total,
    }
  }, [data, priceChangeId])

  const quoteProductByProductId = useMemo(() => {
    const products = priceChangeSubmission?.quoteRequest?.products ?? []
    return new Map(products.map((quoteProduct) => [quoteProduct.productId, quoteProduct]))
  }, [priceChangeSubmission])

  return {
    priceChangeSubmission,
    quoteProductByProductId,
    total,
    pageCount: Math.ceil(total / limit),
    isFetching: fetching,
    refetch,
  }
}
